/**
 * Tjäna pengar — landing page behaviour.
 *
 * One state (goal, sellers, purpose) drives the whole page. Every element with
 * `data-out="<name>"` shows a live value; the server renders the initial values
 * so nothing jumps on load.
 *
 * Buyers per seller use the calculator block's formula and Site Options values
 * (multiplier / divisor, passed in the config), so both always agree:
 *   ceil(round(goal × multiplier) / sellers / divisor)
 *
 * All data and translated strings come from <script id="tp-config"> (built in
 * inc/tjana-pengar.php) — nothing content-related is hardcoded here.
 */

const root = document.querySelector('[data-tp]');
const configEl = document.getElementById('tp-config');

if (root && configEl) {
  init(root, JSON.parse(configEl.textContent));
}

function init(root, cfg) {
  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const $$ = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const nf = new Intl.NumberFormat(cfg.locale);
  const nf2 = new Intl.NumberFormat(cfg.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money = (n) => `${nf.format(Math.round(n))} ${cfg.currency}`;
  const money2 = (n) => `${Math.round(n * 100) % 100 ? nf2.format(n) : nf.format(Math.round(n))} ${cfg.currency}`;
  const count = (n, one, many) => `${nf.format(n)} ${n === 1 ? one : many}`;
  const sprintf = (str, ...args) => args.reduce((s, a, i) => s.replace(`%${i + 1}$s`, a), str).replace('%%', '%');
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  const purposes = Object.fromEntries(cfg.purposes.map((p) => [p.id, p]));
  const state = {
    goal: cfg.state.goal,
    sellers: cfg.state.sellers,
    purpose: purposes[cfg.state.purpose] || null,
    goalTouched: false,
  };

  // ------------------------------------------------------------------ maths

  const perPerson = () => state.goal / state.sellers;
  const salesPerPerson = () => (cfg.share > 0 ? perPerson() / cfg.share : 0);
  const buyersPerSeller = () =>
    Math.max(1, Math.ceil(Math.round(state.goal * cfg.multiplier) / state.sellers / cfg.divisor));
  const seg = () => cfg.segments[state.purpose ? state.purpose.seg : ''] || { people: '', group: '' };

  // ------------------------------------------------------------------ output

  const setOut = (name, value) => {
    $$(`[data-out="${name}"]`, document).forEach((el) => {
      if (el.textContent !== value) el.textContent = value;
    });
  };

  function render() {
    setOut('goal', money(state.goal));
    setOut('sellers', nf.format(state.sellers));
    setOut('people', seg().people);
    setOut('purpose', state.purpose ? state.purpose.label : '');
    setOut('per', money(Math.ceil(perPerson())));
    setOut('sales', money(Math.ceil(salesPerPerson() / 10) * 10));
    setOut('buyers', nf.format(buyersPerSeller()));
    setOut('teamSales', money(Math.round(state.goal / cfg.share / 100) * 100));
    sheet.refresh();
    syncControls();
  }

  // ------------------------------------------------------------------ the basket

  // Small seeded RNG so the same plan always produces the same list.
  function rng(seed) {
    let s = seed >>> 0;
    return () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };
  }

  // Pick a product that fits what is left of this buyer's order (weighted).
  function pickFor(room, r) {
    const cheapest = Math.min(...cfg.products.map((p) => p.price));
    const fits = cfg.products.filter((p) => p.price <= Math.max(room, cheapest));
    const total = fits.reduce((a, p) => a + p.weight, 0);
    let t = r() * total;
    for (const p of fits) {
      t -= p.weight;
      if (t < 0) return p;
    }
    return fits[0];
  }

  // One seller's list: about as many buyers as the calculator says, each
  // spending around the same, until the seller's share is sold.
  function basket() {
    const target = salesPerPerson();
    const n = buyersPerSeller();
    const r = rng(7919 + Math.round(target));
    const names = cfg.buyers.slice();
    for (let i = names.length - 1; i > 0; i--) {
      const j = Math.floor(r() * (i + 1));
      [names[i], names[j]] = [names[j], names[i]];
    }

    const orders = [];
    let sales = 0;
    let items = 0;
    let guard = 0;
    while (sales < target - 1e-9 && guard++ < 500) {
      const last = orders.length >= n - 1;
      const left = orders.length < n ? n - orders.length : 1;
      const aim = ((target - sales) / left) * (last ? 1 : 0.75 + r() * 0.5);
      const cap = last ? 8 : 4; // the last planned buyer tops up the list
      const fill = last ? 1 : 0.85;
      const lines = new Map();
      let sum = 0;
      let c = 0;
      while (c < cap && (c === 0 || sum < aim * fill)) {
        const p = pickFor(aim - sum + 60, r);
        lines.set(p, (lines.get(p) || 0) + 1);
        sum += p.price;
        c++;
      }
      orders.push({ who: names[orders.length % names.length], lines: [...lines], price: sum, items: c });
      sales += sum;
      items += c;
    }
    return { orders, sales, kept: sales * cfg.share, items };
  }

  // ------------------------------------------------------------------ the seller's list

  // Plays once when scrolled into view: buyers appear one at a time, each with
  // a matching segment on the goal line. Later plan changes redraw it instantly.
  const sheet = (() => {
    const el = $('[data-tp-sheet]');
    const goal = $('[data-tp-goal]');
    if (!el || !goal || !cfg.products.length) return { refresh() {} };

    const STEP_MS = 900;
    const STEP_MS_QUIET = 450;
    // Phones: the goal box sits above the list, so the list can't be watched
    // filling up. There the list is drawn complete up front and only the goal
    // line plays ("quiet"), triggered by the goal box. Fewer rows, too.
    const narrow = window.matchMedia('(max-width: 767.98px)');
    const show = () => (narrow.matches ? 3 : 7);
    const START_DELAY = 600;

    const list = $('[data-sheet-orders]', el);
    const countEl = $('[data-sheet-count]', el);
    const soldEl = $('[data-goal-sold]', goal);
    const track = $('[data-goal-track]', goal);
    const segs = $('[data-goal-segs]', goal);
    const gift = $('[data-goal-gift]', goal);
    const meta = $('[data-goal-meta]', goal);
    const done = $('[data-goal-done]', goal);

    let played = false;
    let quiet = false;
    let timer = null;
    let tween = null;
    let shown = 0;
    let sold = 0;

    const buyers = (n) => count(n, cfg.i18n.buyer, cfg.i18n.buyers);

    function row(o, isNew) {
      const li = document.createElement('li');
      li.className = `tp-order${isNew ? ' is-new' : ''}`;
      const img = document.createElement('img');
      img.className = 'tp-order__img';
      img.alt = '';
      img.src = o.lines[0][0].img;
      const text = document.createElement('div');
      const who = document.createElement('p');
      who.className = 'tp-order__who';
      who.textContent = o.who;
      const what = document.createElement('p');
      what.className = 'tp-order__what';
      what.textContent = o.lines.map(([p, q]) => (q > 1 ? `${q} × ` : '') + p.name).join(' + ');
      text.append(who, what);
      const kr = document.createElement('p');
      kr.className = 'tp-order__kr';
      kr.textContent = money(o.price);
      const kept = document.createElement('small');
      kept.textContent = `+${money2(o.price * cfg.share)}`;
      kr.append(kept);
      li.append(img, text, kr);
      return li;
    }

    function moreRow(rest) {
      const li = document.createElement('li');
      li.className = 'tp-order tp-order--more';
      const restSales = rest.reduce((s, o) => s + o.price, 0);
      li.textContent = sprintf(cfg.i18n.more, buyers(rest.length), money(restSales));
      return li;
    }

    function countTo(to) {
      cancelAnimationFrame(tween);
      const from = shown;
      const t0 = performance.now();
      shown = to;
      if (reduced) {
        soldEl.textContent = money(to);
        return;
      }
      const frame = (t) => {
        const k = Math.min((t - t0) / 420, 1);
        soldEl.textContent = money(from + (to - from) * (1 - Math.pow(1 - k, 3)));
        if (k < 1) tween = requestAnimationFrame(frame);
      };
      tween = requestAnimationFrame(frame);
    }

    // One segment per sale, sized to the order, plus a "+548 kr" label that fades.
    function addSegment(amount, fresh) {
      const target = salesPerPerson();
      const w = Math.min(amount / target, Math.max(0, 1 - sold / target));
      sold += amount;
      if (w > 0) {
        const s = document.createElement('span');
        s.className = `tp-goal__seg${fresh ? ' is-new' : ''}`;
        s.style.width = `${w * 100}%`;
        segs.appendChild(s);
        if (fresh && !reduced) {
          const pop = document.createElement('span');
          pop.className = `tp-goal__pop${sold / target > 0.9 ? ' is-end' : ''}`;
          pop.textContent = `+${money(amount)}`;
          pop.style.left = `${((sold - amount / 2) / target) * 100}%`;
          track.appendChild(pop);
          setTimeout(() => pop.remove(), 950);
        }
      }
      gift.classList.toggle('is-hit', sold >= cfg.bonus);
      segs.classList.toggle('is-done', sold >= target);
    }

    function status(b, items) {
      if (!quiet) countEl.textContent = buyers(b);
      meta.textContent = b
        ? `${buyers(b)} · ${count(items, cfg.i18n.product, cfg.i18n.products)}`
        : cfg.i18n.filling;
    }

    function finish(b) {
      $('[data-goal-kept]', done).textContent = money(b.kept);
      $('[data-goal-why]', done).textContent = sprintf(cfg.i18n.why, nf.format(Math.round(cfg.share * 100)), money(b.sales));
      done.hidden = false;
    }

    // Lay the finished list out invisibly and keep its height, so the page
    // below doesn't move while buyers appear.
    function reserve(head, rest) {
      head.forEach((o) => list.appendChild(row(o, false)));
      if (rest.length) list.appendChild(moreRow(rest));
      list.style.minHeight = `${list.offsetHeight}px`;
      list.replaceChildren();
    }

    // The complete list, no animation (phones).
    function fillList(b) {
      list.style.minHeight = '';
      list.replaceChildren();
      b.orders.slice(0, show()).forEach((o) => list.appendChild(row(o, false)));
      const rest = b.orders.slice(show());
      if (rest.length) list.appendChild(moreRow(rest));
      countEl.textContent = buyers(b.orders.length);
    }

    function presize() {
      const b = basket();
      if (narrow.matches) fillList(b);
      else reserve(b.orders.slice(0, show()), b.orders.slice(show()));
    }

    function reset() {
      clearTimeout(timer);
      list.style.minHeight = '';
      list.replaceChildren();
      segs.replaceChildren();
      segs.classList.remove('is-done');
      $$('.tp-goal__pop', track).forEach((p) => p.remove());
      done.hidden = true;
      sold = 0;
      const giftPos = cfg.bonus / salesPerPerson();
      gift.hidden = !cfg.bonus || giftPos >= 1;
      gift.style.left = `${giftPos * 100}%`;
      gift.classList.remove('is-hit');
      countTo(0);
      status(0, 0);
    }

    function drawInstant() {
      quiet = false;
      const b = basket();
      const head = b.orders.slice(0, show());
      const rest = b.orders.slice(show());
      reset();
      head.forEach((o) => {
        list.appendChild(row(o, false));
        addSegment(o.price, false);
      });
      if (rest.length) {
        list.appendChild(moreRow(rest));
        addSegment(rest.reduce((s, o) => s + o.price, 0), false);
      }
      countTo(sold);
      status(b.orders.length, b.items);
      finish(b);
    }

    function play() {
      const b = basket();
      const head = b.orders.slice(0, show());
      let rest = b.orders.slice(show());
      let n = 0;
      let items = 0;
      let i = 0;
      quiet = narrow.matches;
      reset();
      if (quiet) fillList(b);
      else reserve(head, rest);

      const step = () => {
        let li;
        let amount;
        if (i < head.length) {
          const o = head[i++];
          amount = o.price;
          n++;
          items += o.items;
          li = row(o, true);
        } else if (rest.length) {
          amount = rest.reduce((s, o) => s + o.price, 0);
          rest.forEach((o) => {
            n++;
            items += o.items;
          });
          li = moreRow(rest);
          rest = [];
        } else {
          quiet = false;
          finish(b);
          return;
        }
        // The row and its segment light up together (only the segment when quiet).
        if (!quiet) {
          li.classList.add('is-sale');
          list.appendChild(li);
          setTimeout(() => li.classList.remove('is-sale'), 1100);
        }
        addSegment(amount, true);
        countTo(sold);
        status(n, items);
        timer = setTimeout(step, quiet ? STEP_MS_QUIET : STEP_MS);
      };
      step();
    }

    function start() {
      if (played) return;
      played = true;
      window.removeEventListener('scroll', check);
      timer = setTimeout(play, START_DELAY);
    }

    // Fallback in case the observer never fires.
    function check() {
      const r = (narrow.matches ? goal : el).getBoundingClientRect();
      if (r.top < window.innerHeight * 0.45 && r.bottom > 0) start();
    }

    const represize = () => {
      if (!played) {
        list.style.minHeight = '';
        presize();
      }
    };

    reset();
    presize();
    window.addEventListener('resize', represize);
    // The fallback font wraps rows differently — re-measure once the webfont is in.
    if (document.fonts) document.fonts.ready.then(represize);

    if (reduced) {
      played = true;
      drawInstant();
    } else {
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          io.disconnect();
          start();
        }
      }, { threshold: 0.6 });
      io.observe(narrow.matches ? goal : el);
      window.addEventListener('scroll', check, { passive: true });
    }

    return {
      refresh() {
        if (played) drawInstant();
        else {
          reset();
          presize();
        }
      },
    };
  })();

  // ------------------------------------------------------------------ the sentence

  const sentence = $('[data-tp-sentence]');
  const ranges = {
    goal: $('[data-range="goal"]'),
    sellers: $('[data-range="sellers"]'),
  };
  const goalInput = $('[data-goal-input]');
  let openTok = null;

  const fillRange = (r) => r && r.style.setProperty('--fill', `${((r.value - r.min) / (r.max - r.min)) * 100}%`);

  function syncControls() {
    if (ranges.goal) ranges.goal.value = clamp(state.goal, +ranges.goal.min, +ranges.goal.max);
    if (ranges.sellers) ranges.sellers.value = clamp(state.sellers, +ranges.sellers.min, +ranges.sellers.max);
    fillRange(ranges.goal);
    fillRange(ranges.sellers);
    $$('.tp-chip[data-goal]').forEach((c) => c.classList.toggle('is-on', +c.dataset.goal === state.goal));
    $$('.tp-chip[data-sellers]').forEach((c) => c.classList.toggle('is-on', +c.dataset.sellers === state.sellers));
    $$('.tp-chip[data-purpose]').forEach((c) =>
      c.classList.toggle('is-on', !!state.purpose && c.dataset.purpose === state.purpose.id),
    );
    if (openTok) place(openTok);
  }

  // Anchor the popup under the word that opened it, kept inside the viewport.
  function place(tok) {
    const pop = $(`#tp-pop-${tok.dataset.pop}`);
    const w = sentence.getBoundingClientRect();
    const t = tok.getBoundingClientRect();
    const right = document.documentElement.clientWidth - 16 - w.left;
    const left = Math.max(-w.left + 16, Math.min(t.left - w.left, right - pop.offsetWidth));
    pop.style.left = `${left}px`;
    pop.style.top = `${t.bottom - w.top + 12}px`;
    const arrow = clamp(t.left - w.left - left + Math.min(t.width / 2, 40) - 7, 16, pop.offsetWidth - 30);
    pop.style.setProperty('--arrow', `${arrow}px`);
  }

  function openPop(tok) {
    if (openTok === tok) {
      closePop();
      return;
    }
    closePop();
    openTok = tok;
    tok.setAttribute('aria-expanded', 'true');
    const pop = $(`#tp-pop-${tok.dataset.pop}`);
    pop.hidden = false;
    place(tok);
    const first = $('.tp-range', pop) || $('.tp-chip.is-on', pop) || $('.tp-chip', pop);
    if (first) first.focus({ preventScroll: true });
  }

  function closePop(returnFocus) {
    if (!openTok) return;
    $(`#tp-pop-${openTok.dataset.pop}`).hidden = true;
    openTok.setAttribute('aria-expanded', 'false');
    if (returnFocus) openTok.focus();
    openTok = null;
  }

  function setGoal(v) {
    state.goal = clamp(v, 1000, 1000000);
    state.goalTouched = true;
    render();
  }

  function setSellers(v) {
    state.sellers = clamp(v, 1, 300);
    render();
  }

  // Swap the hero image (and the "Ert lag" poster) to the purpose's photo.
  function setImage(src) {
    if (!src) return;
    $$('[data-tp-purpose-img]').forEach((img) => {
      if (img.getAttribute('src') === src) return;
      if (reduced || !img.closest('.tp-hero__media')) {
        img.src = src;
        return;
      }
      img.classList.add('is-out');
      setTimeout(() => {
        img.src = src;
        img.classList.remove('is-out');
      }, 250);
    });
  }

  function setPurpose(p) {
    state.purpose = p;
    if (p.goal && !state.goalTouched) state.goal = p.goal;
    setImage(p.image || cfg.fallbackImage);
    reorderPosters();
    render();
  }

  if (sentence) {
    $$('.tp-tok', sentence).forEach((t) =>
      t.addEventListener('click', (e) => {
        e.stopPropagation();
        openPop(t);
      }),
    );
    $$('.tp-pop', sentence).forEach((p) => p.addEventListener('click', (e) => e.stopPropagation()));
    $$('[data-pop-close]', sentence).forEach((b) => b.addEventListener('click', () => closePop(true)));
    document.addEventListener('click', () => closePop());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePop(true);
    });
    window.addEventListener('resize', () => openTok && place(openTok));

    if (ranges.goal) ranges.goal.addEventListener('input', () => setGoal(+ranges.goal.value));
    if (ranges.sellers) ranges.sellers.addEventListener('input', () => setSellers(+ranges.sellers.value));
    $$('.tp-chip[data-goal]').forEach((c) =>
      c.addEventListener('click', () => {
        setGoal(+c.dataset.goal);
        if (goalInput) goalInput.value = '';
      }),
    );
    $$('.tp-chip[data-sellers]').forEach((c) => c.addEventListener('click', () => setSellers(+c.dataset.sellers)));
    $$('.tp-pop__pm').forEach((b) => b.addEventListener('click', () => setSellers(state.sellers + +b.dataset.step)));
    $$('.tp-chip[data-purpose]').forEach((c) =>
      c.addEventListener('click', () => {
        const p = purposes[c.dataset.purpose];
        if (p) setPurpose(p);
        closePop(true);
      }),
    );

    if (goalInput) {
      goalInput.addEventListener('input', () => {
        const v = parseInt(goalInput.value.replace(/\D/g, ''), 10) || 0;
        goalInput.value = v ? nf.format(v) : '';
        if (v >= 1000) setGoal(v);
      });
      goalInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') closePop(true);
      });
    }
  }

  // ------------------------------------------------------------------ proof

  const posters = $('[data-tp-posters]');

  // Posters matching the chosen group type first; "Ert lag" always last.
  function reorderPosters() {
    if (!posters || !state.purpose) return;
    const all = $$('.tp-poster', posters);
    const you = all.filter((p) => p.dataset.seg === 'you');
    const match = all.filter((p) => p.dataset.seg === state.purpose.seg);
    const others = all.filter((p) => !match.includes(p) && !you.includes(p));
    [...match, ...others, ...you].forEach((p) => posters.appendChild(p));
    posters.scrollLeft = 0;
  }

  if (posters) {
    $$('.tp-poster__flip', posters).forEach((b) =>
      b.addEventListener('click', () => {
        const p = b.closest('.tp-poster');
        const open = p.classList.toggle('is-open');
        b.setAttribute('aria-expanded', String(open));
        b.firstElementChild.textContent = open ? cfg.i18n.back : b.dataset.label;
      }),
    );
  }

  // ------------------------------------------------------------------ swipe-row pagers

  // Prev/next buttons for each swipe row: step to the next/previous card at the
  // row's snap edge, disable at the ends, hide while the row doesn't scroll.
  $$('[data-pager]').forEach((nav) => {
    const row = document.getElementById(nav.dataset.pager);
    if (!row) return;
    const [prev, next] = $$('[data-pager-step]', nav);
    const edge = () => row.getBoundingClientRect().left + (parseFloat(getComputedStyle(row).scrollPaddingLeft) || 0);

    function go(dir) {
      const base = edge();
      const items = [...row.children];
      const target = dir > 0
        ? items.find((el) => el.getBoundingClientRect().left > base + 4)
        : items.filter((el) => el.getBoundingClientRect().left < base - 4).pop();
      if (target) row.scrollBy({ left: target.getBoundingClientRect().left - base, behavior: reduced ? 'auto' : 'smooth' });
    }

    let raf = 0;
    function update() {
      raf = 0;
      const max = row.scrollWidth - row.clientWidth;
      if (nav.classList.contains('tp-pager')) nav.hidden = max <= 2;
      prev.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollLeft >= max - 2;
    }
    const queue = () => raf || (raf = requestAnimationFrame(update));

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    row.addEventListener('scroll', queue, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(row);
    update();
  });

  // ------------------------------------------------------------------ products: rotating quotes

  const quote = $('[data-tp-quote]');
  if (quote && cfg.quotes.length > 1 && !reduced) {
    let qi = 0;
    setInterval(() => {
      quote.classList.add('is-out');
      setTimeout(() => {
        qi = (qi + 1) % cfg.quotes.length;
        $('blockquote', quote).textContent = `”${cfg.quotes[qi].quote}”`;
        $('figcaption', quote).textContent = cfg.quotes[qi].author;
        quote.classList.remove('is-out');
      }, 350);
    }, 5000);
  }

  // ------------------------------------------------------------------ FAQ (tabs)

  const pills = $$('.tp-faq__pill');
  function selectPill(pill) {
    pills.forEach((x) => {
      const on = x === pill;
      x.classList.toggle('is-on', on);
      x.setAttribute('aria-selected', String(on));
      x.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(x.getAttribute('aria-controls'));
      if (panel) {
        panel.classList.toggle('is-on', on);
        panel.hidden = !on;
      }
    });
  }
  pills.forEach((pill, i) => {
    pill.addEventListener('click', () => selectPill(pill));
    pill.addEventListener('keydown', (e) => {
      const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
      if (!d) return;
      e.preventDefault();
      const next = pills[(i + d + pills.length) % pills.length];
      selectPill(next);
      next.focus();
    });
  });

  // Phones: the toggle list shows 3 questions, the button reveals the rest.
  const faqMore = $('[data-faq-more]');
  if (faqMore) {
    faqMore.addEventListener('click', () => {
      const extra = $$('[data-faq-extra]');
      extra.forEach((d) => { d.hidden = false; });
      faqMore.parentElement.remove();
      if (extra[0]) $('summary', extra[0]).focus({ preventScroll: true });
    });
  }

  // ------------------------------------------------------------------ roles (organiser / sellers tabs)

  const roleTabs = $$('.tp-roles__tab');
  function selectRole(tab) {
    roleTabs.forEach((t) => {
      const on = t === tab;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }
  roleTabs.forEach((tab, i) => {
    tab.addEventListener('click', () => selectRole(tab));
    tab.addEventListener('keydown', (e) => {
      const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      if (!d) return;
      e.preventDefault();
      const next = roleTabs[(i + d + roleTabs.length) % roleTabs.length];
      selectRole(next);
      next.focus();
    });
  });

  // ------------------------------------------------------------------ doors (sign-up)

  const doorsSection = document.getElementById('kom-igang');
  const doors = $$('.tp-door');

  function selectDoor(id) {
    doors.forEach((d) => {
      const on = d.dataset.door === id;
      d.classList.toggle('is-on', on);
      d.setAttribute('aria-selected', String(on));
      const panel = document.getElementById(d.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }

  function focusDoor(id) {
    selectDoor(id);
    const panel = document.getElementById(`tp-door-panel-${id}`);
    const input = panel && panel.querySelector('input:not([type="hidden"]), select, textarea');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 700);
  }

  doors.forEach((d) => d.addEventListener('click', () => selectDoor(d.dataset.door)));

  // "Starta försäljning" anywhere → start door; any #infopaket link → info door.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link || !doorsSection) return;
    const hash = link.getAttribute('href');
    if (hash === '#infopaket') {
      e.preventDefault();
      focusDoor('info');
      doorsSection.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
      history.pushState(null, '', '#infopaket');
    } else if (hash === '#kom-igang') {
      focusDoor('start'); // main.js handles the smooth scroll
    }
  });

  if (location.hash === '#infopaket') selectDoor('info');

  // ------------------------------------------------------------------ floating bar

  const nudge = $('[data-tp-nudge]');
  if (nudge && 'IntersectionObserver' in window) {
    const btn = $('[data-nudge-btn]', nudge);
    const stages = {
      1: { label: nudge.dataset.exploreLabel, href: nudge.dataset.exploreHref },
      2: { label: nudge.dataset.startLabel, href: '#kom-igang' },
    };
    const sections = $$('[data-cta]');
    const visible = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => visible.set(e.target, e.isIntersecting));
      const on = sections.filter((s) => visible.get(s)).map((s) => +s.dataset.cta);
      if (!on.length) return;
      const s = stages[Math.max(...on)];
      nudge.classList.toggle('is-on', !!(s && s.label));
      if (s && s.label) {
        btn.textContent = s.label;
        btn.setAttribute('href', s.href);
      }
    }, { rootMargin: '-45% 0px -45% 0px' });
    sections.forEach((s) => io.observe(s));
  }

  // ------------------------------------------------------------------ hero height

  // The hero fills the first screen minus the header and the trust bar (see
  // $hero-h in the SCSS). Their real heights vary (trust items wrap), so
  // measure them instead of trusting fixed values.
  const header = document.querySelector('.site-header');
  const trust = $('.tp-trust');
  if ('ResizeObserver' in window) {
    const measure = () => {
      if (header) root.style.setProperty('--tp-header-h', `${header.offsetHeight}px`);
      root.style.setProperty('--tp-trust-h', `${trust ? trust.offsetHeight : 0}px`);
    };
    const ro = new ResizeObserver(measure);
    [header, trust].forEach((el) => el && ro.observe(el));
    measure();
  }

  // ------------------------------------------------------------------ init

  render();
}
